// =============================================================================
// Active-company scope guard (defense-in-depth for multi-tenant queries).
//
// Problem: many reference getters take `companyId?: string | null` and, when the
// caller forgets to pass it, return rows for ALL companies the user belongs to
// (the user is a member of every group company), producing duplicate dropdown
// entries. RLS cannot fix this because the user IS authorised for all companies.
//
// Guard: CompanyContext publishes the active company id here. Getters resolve
// their scope via `resolveCompanyScope(companyId)`:
//   • companyId === undefined  → fall back to the ACTIVE company (the guard)
//   • companyId === null        → explicit "all companies" (consolidated view)
//   • companyId is a string     → that specific company
// So a forgotten argument scopes to the active company instead of leaking all.
// =============================================================================

let _activeCompanyId: string | null = null;

/** Called by CompanyContext whenever the active company changes. */
export function setActiveCompanyScope(id: string | null): void {
  _activeCompanyId = id;
}

/** The currently active company id (null in consolidated view). */
export function getActiveCompanyScope(): string | null {
  return _activeCompanyId;
}

/**
 * Resolve the company a query should be scoped to.
 * Distinguishes "argument omitted" (undefined → active company) from an
 * explicit `null` (consolidated → no company filter).
 */
export function resolveCompanyScope(companyId?: string | null): string | null {
  return companyId === undefined ? _activeCompanyId : companyId;
}
